import React, {useMemo, useState} from 'react'
import {isMailtoURI, isURL} from 'validator'

import {Feedback, useToast} from '@ua/design-system'
import {Checkbox} from '@ua/design-system/components/checkbox'
import {Input} from '@ua/design-system/components/input'
import {Modal} from '@ua/design-system/components/modal'
import {Select} from '@ua/design-system/components/select'
import {Textarea} from '@ua/design-system/components/textarea'

import {isValidEmail} from 'airship/components/Validator/validators'

import {FieldValidation, ValidationScope} from '../Validator'
import {postForm} from './api'
import {FEEDBACK_SELECT_OPTIONS} from './constants'
import FormActions from './FormActions'
import {CustomerFeedbackForm} from './type'

type Props = {
  isOpen: boolean
  onClose: () => void
  name?: string
  email?: string
}

export function textContainsUrl(text: string): boolean {
  const hasUrl = (s: string): boolean => {
    // remove any special characters from the start and end of the string
    const stringToCheck = s.replace(/^[\W_]+|[\W_]+$/g, '')
    return (
      isURL(stringToCheck, {require_protocol: true}) ||
      isMailtoURI(s) ||
      s.startsWith('tel:')
    )
  }
  const splitText = text.replace(/\n/g, ' ').split(' ')
  // split words to filter out urls that might be linked with commas
  return splitText.some(
    (word) => hasUrl(word) || word.split(',').some((w) => hasUrl(w))
  )
}

const CustomerFeedbackModal = ({
  isOpen,
  onClose,
  name,
  email,
}: Props): JSX.Element => {
  const [formData, setFormData] = useState<CustomerFeedbackForm>({
    name: name ?? '',
    email: email ?? '',
    section: '',
    message: '',
    scheduleCall: 'no',
    url: window.location.href,
  })
  const [errorForm, setErrorForm] = useState<boolean>(false)
  const toast = useToast()

  const handleOnSubmit = async (): Promise<void> => {
    const res = await postForm(formData)
    if (res.statusCode === 200) {
      toast.showToast({
        description: 'Thank you for your feedback!',
        variant: 'success',
      })
      onClose()
    } else if (res.statusCode === 429) {
      toast.showToast({
        description: 'You can only submit the form once per minute.',
        variant: 'warning',
      })
    } else {
      toast.showToast({
        description: 'Oops, something went wrong. Please try again later.',
        variant: 'error',
      })
    }
  }

  const messageHasURL = useMemo(() => {
    const {message} = formData
    return textContainsUrl(message)
  }, [formData])

  return (
    <Modal
      isOpen={isOpen}
      footer={
        <FormActions
          onCancel={onClose}
          onSubmit={handleOnSubmit}
          isDisabled={!errorForm || messageHasURL}
        />
      }
      heading="Feedback for the product team"
      onClose={onClose}
      size="md"
      closeOnOverlayClick={false}
    >
      <ValidationScope onValid={setErrorForm}>
        <div className="u-space-items-2">
          <Input
            label="name"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({...prev, name: e.target.value}))
            }
          />

          <FieldValidation
            id="email"
            value={formData.email}
            validators={[isValidEmail()]}
            asyncValidators={[]}
          >
            <Input
              isRequired
              label="Email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({...prev, email: e.target.value}))
              }
            />
          </FieldValidation>

          <Select
            label="What do you want to give feedback on ?"
            placeholder="Select"
            onChange={(e) =>
              setFormData((prev) => ({...prev, section: e.target.value}))
            }
          >
            {FEEDBACK_SELECT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Textarea
            label="Message"
            onChange={(e) =>
              setFormData((prev) => ({...prev, message: e.target.value}))
            }
          />
          {messageHasURL && (
            <Feedback variant="error" size="sm">
              <p>URLs cannot be used in the message</p>
            </Feedback>
          )}

          <Checkbox
            onChange={() =>
              setFormData((prev) => ({
                ...prev,
                scheduleCall: prev.scheduleCall === 'no' ? 'yes' : 'no',
              }))
            }
          >
            Schedule 30 minutes to speak directly with our team
          </Checkbox>
        </div>
      </ValidationScope>
    </Modal>
  )
}

export default CustomerFeedbackModal
